import {useCallback, useContext, useMemo} from 'react';
import {FiltersContext} from '../contexts';
import {trackEvent} from '@lib/infrastructure';
import {FilterConfigSections, FilterSelectionSections, FilterSliceKeyType, remove} from '@lib/utils';

/* For more information on filtering, see https://dev.azure.com/mapiq/Mapiq/_wiki/wikis/Mapiq.wiki/1536/-WiP-Filtering */
type ReturnType = {
  config: FilterConfigSections;
  setConfig: (config: FilterConfigSections) => void;
  selectedFilters: FilterSelectionSections;
  resetFilters: () => void;
  getInitFilterSelection: (config: FilterConfigSections) => FilterSelectionSections;
  /**
   * Applies filters if they are respecting rules defined in `config`, on priciple 'all or nothing'.
   * @param newFilters Full set of selected filter options.
   * @returns `true` if filters are applied, `false` otherwise
   */
  applyFilters: (newFilters: FilterSelectionSections) => boolean;
  /**
   * Removes one selected filter option from specific section if its respecting rules defined in `config`.
   * @param sectionId
   * @param filterId
   * @returns `true` if filter is removed, `false` otherwise
   */
  removeFilter: (sectionId: string, filterId: string) => boolean;
  cacheKey?: string;
  setCacheKey: (newKey: string, resetSelection: FilterSelectionSections) => void;
};

export const useFilters = (sliceKey: FilterSliceKeyType): ReturnType => {
  const {
    configSlices,
    setConfigSlice,
    selectionSlices,
    setSelectionSlice,
    getInitFilterSelection,
    cacheKeySlices,
    setCacheKeySlice,
  } = useContext(FiltersContext);

  const currentConfigSlice = configSlices[sliceKey];
  const currentSelectionSlice = selectionSlices[sliceKey];
  const cacheKey = cacheKeySlices[sliceKey];

  const setConfig = useCallback(
    (config: FilterConfigSections) => {
      setConfigSlice(sliceKey, config);
    },
    [setConfigSlice, sliceKey],
  );

  const resetFilters = useCallback(() => {
    if (!currentConfigSlice) return;

    setSelectionSlice(sliceKey, getInitFilterSelection(currentConfigSlice));
    trackEvent(`${sliceKey}__FilterReset`);
  }, [currentConfigSlice, getInitFilterSelection, setSelectionSlice, sliceKey]);

  const applyFilters = useCallback(
    (newFilters: FilterSelectionSections) => {
      const anyMandatoryWithoutValue = Object.entries(newFilters).some(([key, newValue]) => {
        const {type} = currentConfigSlice?.[key] ?? {};
        return type === 'singleMandatory' && !newValue?.length;
      });
      if (anyMandatoryWithoutValue) return false;

      setSelectionSlice(sliceKey, newFilters);
      trackEvent(`${sliceKey}__ApplyFilters`, newFilters);
      return true;
    },
    [currentConfigSlice, setSelectionSlice, sliceKey],
  );

  const removeFilter = useCallback(
    (sectionId: string, filterId: string) => {
      const sectionConfig = currentConfigSlice?.[sectionId];
      if (!currentSelectionSlice) return false;
      if (!sectionConfig) return false;

      if (sectionConfig.type === 'singleMandatory') return false;

      const activeFilters = currentSelectionSlice[sectionId] ?? [];
      const itemIndex = activeFilters.findIndex((af) => af === filterId);
      if (itemIndex < 0) return false;

      setSelectionSlice(sliceKey, {...currentSelectionSlice, [sectionId]: remove(activeFilters, itemIndex)});
      trackEvent(`${sliceKey}__DeleteFilter`, {sectionId, filterId});
      return true;
    },
    [currentConfigSlice, currentSelectionSlice, setSelectionSlice, sliceKey],
  );

  const setCacheKey = useCallback(
    (newKey: string, resetSelection: FilterSelectionSections) => {
      setCacheKeySlice(sliceKey, newKey, resetSelection);
    },
    [setCacheKeySlice, sliceKey],
  );

  return useMemo(
    () => ({
      config: currentConfigSlice ?? {},
      setConfig,
      selectedFilters: currentSelectionSlice ?? getInitFilterSelection(currentConfigSlice ?? {}),
      resetFilters,
      getInitFilterSelection,
      applyFilters,
      removeFilter,
      cacheKey,
      setCacheKey,
    }),
    [
      applyFilters,
      currentConfigSlice,
      currentSelectionSlice,
      getInitFilterSelection,
      removeFilter,
      resetFilters,
      setConfig,
      cacheKey,
      setCacheKey,
    ],
  );
};
